import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudSyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, Tag, Typography } from 'antd';
import { type IntlShape, useIntl } from 'react-intl';
import { CaptureSessionMeterRecordRow, MeterRecordListRow } from '@/components/MeterRecordSummary';
import { messages, syncQueueStatusMessages, workflowTypeMessages } from '@/features/sync/messages';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { isRetryableSyncQueueRecord, readSyncErrorMessage } from '@/shared/db/repositories/sync-queue-records';
import { SYNC_QUEUE_STATUSES, type SyncQueueRecord } from '@/shared/db/schema';

export function SyncRecordRow({
  onOpen,
  onRetry,
  record,
  session,
}: {
  onOpen: () => void;
  onRetry: () => void;
  record: SyncQueueRecord;
  session?: CaptureSession;
}) {
  const intl = useIntl();
  const errorMessage = readSyncErrorMessage({ errorCode: record.lastErrorCode, session, status: record.status }, intl);
  const actions = isRetryableSyncQueueRecord(record)
    ? [
        <Button
          key="retry"
          onClick={event => {
            event.stopPropagation();
            onRetry();
          }}
          type="link"
        >
          {intl.formatMessage(messages.syncRetry)}
        </Button>,
      ]
    : [];
  if (session) {
    return (
      <CaptureSessionMeterRecordRow
        actions={actions}
        detailsFallback={intl.formatMessage(messages.metersNoPlacementDetails)}
        extraLines={errorMessage ? <Typography.Text type="danger">{errorMessage}</Typography.Text> : null}
        onOpen={onOpen}
        session={session}
        statusTag={<SyncStatusTag record={record} />}
      />
    );
  }

  const summary = {
    details: '',
    identity: '',
    title: formatWorkflowType(intl, record.workflowType),
  };

  return (
    <MeterRecordListRow
      actions={actions}
      detailsFallback={intl.formatMessage(messages.metersNoPlacementDetails)}
      extraLines={errorMessage ? <Typography.Text type="danger">{errorMessage}</Typography.Text> : null}
      onOpen={onOpen}
      rightExtra={<Typography.Text type="secondary">{formatTimestamp(record.updatedAt)}</Typography.Text>}
      summary={summary}
      titleSuffix={<SyncStatusTag record={record} />}
    />
  );
}

function SyncStatusTag({ record }: { record: SyncQueueRecord }) {
  const intl = useIntl();
  const statusConfig: Record<
    SyncQueueRecord['status'],
    {
      color: string;
      icon: JSX.Element;
    }
  > = {
    [SYNC_QUEUE_STATUSES.conflict]: { color: 'orange', icon: <ExclamationCircleOutlined /> },
    [SYNC_QUEUE_STATUSES.needsReview]: { color: 'orange', icon: <ExclamationCircleOutlined /> },
    [SYNC_QUEUE_STATUSES.queued]: { color: 'blue', icon: <ClockCircleOutlined /> },
    [SYNC_QUEUE_STATUSES.syncFailed]: { color: 'red', icon: <ExclamationCircleOutlined /> },
    [SYNC_QUEUE_STATUSES.syncing]: { color: 'processing', icon: <CloudSyncOutlined /> },
    [SYNC_QUEUE_STATUSES.synced]: { color: 'green', icon: <CheckCircleOutlined /> },
  };
  const config = statusConfig[record.status];

  return (
    <Tag color={config.color} icon={config.icon} style={{ marginInlineEnd: 0, maxWidth: '100%', whiteSpace: 'normal' }}>
      {formatSyncQueueStatus(intl, record.status)}
    </Tag>
  );
}

function formatWorkflowType(intl: IntlShape, workflowType: SyncQueueRecord['workflowType']) {
  return intl.formatMessage(workflowTypeMessages[workflowType]);
}

function formatSyncQueueStatus(intl: IntlShape, status: SyncQueueRecord['status']) {
  return intl.formatMessage(syncQueueStatusMessages[status]);
}

function formatTimestamp(timestamp: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
  }).format(new Date(timestamp));
}
